import { useMemo } from "react";
import type { JSX } from "react";
import { useAnalytics } from "../../analytics/provider";
import { useAiModels } from "./hooks/useAiModels";
import { PageSectionHeader } from "../../components/ui";
import { GroupCard, ModelPicker, ModelRow } from "./aiModelsShared";

// Models settings page: add the models the Magnis Agent may use and pick the
// default. Providers live on their own page; the agent on its own page.
export function ModelsPanel(): JSX.Element {
  const analytics = useAnalytics();
  const isHostedDemo = analytics.runtimeSurface === "hosted_demo";
  const {
    providers,
    models,
    defaults,
    loading,
    loadError,
    enableModel,
    deleteModel,
    setDefault,
    catalogModels,
  } = useAiModels();

  // The picker only offers models whose provider is connected (has a key or a
  // subscription) — enabling one from an unconnected provider would 400.
  const usableProviderIds = useMemo(
    () =>
      new Set(
        providers
          .filter((p) => p.api_key_set || p.auth_kind === "codex_subscription")
          .map((p) => p.id),
      ),
    [providers],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-content-tertiary text-sm">
        Loading...
      </div>
    );
  }

  const chatDefaultId = defaults.find((d) => d.capability === "chat")?.model_id;
  const chatDefault = models.find((model) => model.id === chatDefaultId);
  const chatProvider = providers.find(
    (provider) => provider.id === chatDefault?.provider_id,
  );

  return (
    <div className="px-6 py-5 space-y-4">
      <PageSectionHeader
        title="Models"
        description={
          isHostedDemo
            ? "The Hosted Demo uses a model configured by Magnis."
            : "Add the models the Magnis Agent can use, and choose its default."
        }
      />

      {loadError !== null && <span className="text-xs text-red-400 block">{loadError}</span>}

      {isHostedDemo ? (
        <div className="rounded-xl bg-surface-tertiary border border-edge px-4 py-3">
          <div className="text-xs font-medium text-content-tertiary">
            Active demo model
          </div>
          {chatDefault && chatProvider ? (
            <div className="mt-1 flex items-center gap-2">
              <span className="text-sm font-medium text-content">
                {chatDefault.name}
              </span>
              <span className="text-xs text-content-tertiary">
                {chatProvider.name}
              </span>
            </div>
          ) : (
            <div className="mt-1 text-xs text-red-400" role="alert">
              The Hosted Demo chat model is not configured.
            </div>
          )}
          <div className="mt-2 text-xs text-content-tertiary">
            Managed by Hosted Demo. Model changes are unavailable.
          </div>
        </div>
      ) : (
        <>
          <ModelPicker
            enabledModels={models}
            connectedProviderIds={usableProviderIds}
            catalogModels={catalogModels}
            onEnable={async (row) => {
              await enableModel({
                provider_id: row.provider_id,
                model_id: row.model_id,
              });
            }}
          />

          {models.length > 0 && (
            <GroupCard>
              {models.map((model) => (
                <ModelRow
                  key={model.id}
                  model={model}
                  isDefault={model.id === chatDefaultId}
                  onSetDefault={() => setDefault("chat", model.id)}
                  onDelete={() => deleteModel(model.id)}
                />
              ))}
            </GroupCard>
          )}
        </>
      )}
    </div>
  );
}
